// AI Agent Service - Database interactions for text chat agents
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  AgentConfig,
  AgentType,
  ModelType,
  PromptVersion,
  PerformanceMetrics
} from '@/types/ai-admin';

const log = createComponentLogger('AIAgentService', 'ai-agents');

// Map database model to frontend type
interface DatabaseAgent {
  id: string;
  organization_id: string;
  agent_key: string;
  name: string;
  description: string | null;
  icon: string;
  model: string;
  temperature: number;
  max_tokens: number;
  system_prompt: string;
  prompt_version: number;
  rag_config: {
    topK: number;
    semanticWeight: number;
    keywordWeight: number;
    similarityThreshold: number;
    maxChunkSize: number;
    chunkOverlap: number;
    rrfK: number;
  };
  enabled_documents: string[];
  document_categories: string[];
  stats: {
    totalQueries: number;
    avgCost: number;
    avgResponseTime: number;
    avgTokensInput: number;
    avgTokensOutput: number;
    successRate: number;
    avgUserRating: number | null;
    lastUsedAt: string | null;
  };
  is_active: boolean;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface DatabasePromptVersion {
  id: string;
  agent_id: string;
  version: number;
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
  rag_config: any;
  performance_metrics: {
    queryCount: number;
    avgRating: number | null;
    avgResponseTime: number | null;
    avgCost: number;
  };
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  notes: string | null;
}

// Convert database agent to frontend format
function mapDatabaseToAgentConfig(dbAgent: DatabaseAgent): AgentConfig {
  return {
    id: dbAgent.id,
    agentType: dbAgent.agent_key as AgentType,
    name: dbAgent.name,
    description: dbAgent.description || '',
    model: dbAgent.model as ModelType,
    temperature: dbAgent.temperature,
    maxTokens: dbAgent.max_tokens,
    systemPrompt: dbAgent.system_prompt,
    currentPromptVersion: dbAgent.prompt_version,
    isActive: dbAgent.is_active,
    isDefault: dbAgent.is_default,
    ragConfig: {
      topK: dbAgent.rag_config.topK,
      similarityThreshold: dbAgent.rag_config.similarityThreshold,
      semanticWeight: dbAgent.rag_config.semanticWeight,
      keywordWeight: dbAgent.rag_config.keywordWeight,
      maxChunkSize: dbAgent.rag_config.maxChunkSize,
      chunkOverlap: dbAgent.rag_config.chunkOverlap
    },
    enabledDocuments: dbAgent.enabled_documents,
    stats: {
      totalQueries: dbAgent.stats.totalQueries,
      avgCost: dbAgent.stats.avgCost,
      avgResponseTime: dbAgent.stats.avgResponseTime,
      avgTokensInput: dbAgent.stats.avgTokensInput,
      avgTokensOutput: dbAgent.stats.avgTokensOutput,
      successRate: dbAgent.stats.successRate,
      avgUserRating: dbAgent.stats.avgUserRating,
      lastUsedAt: dbAgent.stats.lastUsedAt ? new Date(dbAgent.stats.lastUsedAt) : null
    },
    promptVersions: [], // Will be loaded separately
    createdAt: new Date(dbAgent.created_at),
    updatedAt: new Date(dbAgent.updated_at)
  };
}

// Convert database prompt version to frontend format
function mapDatabaseToPromptVersion(dbVersion: DatabasePromptVersion): PromptVersion {
  return {
    id: dbVersion.id,
    agentId: dbVersion.agent_id,
    version: dbVersion.version,
    prompt: dbVersion.system_prompt,
    model: dbVersion.model as ModelType,
    temperature: dbVersion.temperature,
    maxTokens: dbVersion.max_tokens,
    isActive: dbVersion.is_active,
    createdAt: new Date(dbVersion.created_at),
    notes: dbVersion.notes || undefined,
    performanceMetrics: dbVersion.performance_metrics ? {
      queryCount: dbVersion.performance_metrics.queryCount,
      avgRating: dbVersion.performance_metrics.avgRating || 0,
      avgResponseTime: dbVersion.performance_metrics.avgResponseTime || 0,
      avgCost: dbVersion.performance_metrics.avgCost
    } : undefined
  };
}

// Get all agents for the current organization
export async function getAgentConfigs(): Promise<AgentConfig[]> {
  try {
    log.info('Loading agent configurations');

    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: userData } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userData?.organization_id) {
      throw new Error('User has no organization');
    }

    // Load agents
    const { data: agents, error } = await supabase
      .from('ai_text_chat_agents')
      .select('*')
      .eq('organization_id', userData.organization_id)
      .order('name');

    if (error) {
throw error;
}

    // Load prompt versions for each agent
    const agentConfigs = await Promise.all(
      (agents || []).map(async (agent: DatabaseAgent) => {
        const config = mapDatabaseToAgentConfig(agent);

        // Load prompt versions
        const { data: versions } = await supabase
          .from('ai_text_chat_prompt_versions')
          .select('*')
          .eq('agent_id', agent.id)
          .order('version', { ascending: false });

        if (versions) {
          config.promptVersions = versions.map(mapDatabaseToPromptVersion);
        }

        return config;
      })
    );

    log.info('Agent configurations loaded', { count: agentConfigs.length });
    return agentConfigs;
  } catch (error) {
    log.error('Failed to load agent configurations', {}, error as Error);
    throw error;
  }
}

// Get single agent configuration
export async function getAgentConfig(agentId: string): Promise<AgentConfig | null> {
  try {
    const { data: agent, error } = await supabase
      .from('ai_text_chat_agents')
      .select('*')
      .eq('id', agentId)
      .single();

    if (error) {
throw error;
}
    if (!agent) {
return null;
}

    const config = mapDatabaseToAgentConfig(agent as DatabaseAgent);

    // Load prompt versions
    const { data: versions } = await supabase
      .from('ai_text_chat_prompt_versions')
      .select('*')
      .eq('agent_id', agentId)
      .order('version', { ascending: false });

    if (versions) {
      config.promptVersions = versions.map(mapDatabaseToPromptVersion);
    }

    return config;
  } catch (error) {
    log.error('Failed to load agent configuration', { agentId }, error as Error);
    throw error;
  }
}

// Update agent configuration
export async function updateAgentConfig(
  agentId: string,
  updates: Partial<AgentConfig>
): Promise<AgentConfig> {
  try {
    log.info('Updating agent configuration', { agentId });

    const dbUpdates: any = {};

    if (updates.name !== undefined) {
dbUpdates.name = updates.name;
}
    if (updates.description !== undefined) {
dbUpdates.description = updates.description;
}
    if (updates.model !== undefined) {
dbUpdates.model = updates.model;
}
    if (updates.temperature !== undefined) {
dbUpdates.temperature = updates.temperature;
}
    if (updates.maxTokens !== undefined) {
dbUpdates.max_tokens = updates.maxTokens;
}
    if (updates.systemPrompt !== undefined) {
dbUpdates.system_prompt = updates.systemPrompt;
}
    if (updates.isActive !== undefined) {
dbUpdates.is_active = updates.isActive;
}
    if (updates.enabledDocuments !== undefined) {
dbUpdates.enabled_documents = updates.enabledDocuments;
}

    if (updates.ragConfig) {
      dbUpdates.rag_config = {
        topK: updates.ragConfig.topK,
        semanticWeight: updates.ragConfig.semanticWeight,
        keywordWeight: updates.ragConfig.keywordWeight,
        similarityThreshold: updates.ragConfig.similarityThreshold,
        maxChunkSize: updates.ragConfig.maxChunkSize,
        chunkOverlap: updates.ragConfig.chunkOverlap,
        rrfK: 60 // Default value
      };
    }

    dbUpdates.updated_at = new Date().toISOString();

    const { data: agent, error } = await supabase
      .from('ai_text_chat_agents')
      .update(dbUpdates)
      .eq('id', agentId)
      .select()
      .single();

    if (error) {
throw error;
}

    log.info('Agent configuration updated successfully', { agentId });
    return mapDatabaseToAgentConfig(agent as DatabaseAgent);
  } catch (error) {
    log.error('Failed to update agent configuration', { agentId }, error as Error);
    throw error;
  }
}

// Create new prompt version
export async function createPromptVersion(
  agentId: string,
  systemPrompt: string,
  notes?: string
): Promise<PromptVersion> {
  try {
    log.info('Creating new prompt version', { agentId });

    // Get current agent config
    const { data: agent } = await supabase
      .from('ai_text_chat_agents')
      .select('model, temperature, max_tokens, rag_config, prompt_version')
      .eq('id', agentId)
      .single();

    if (!agent) {
throw new Error('Agent not found');
}

    const newVersion = agent.prompt_version + 1;

    // Create new version
    const { data: version, error } = await supabase
      .from('ai_text_chat_prompt_versions')
      .insert({
        agent_id: agentId,
        version: newVersion,
        system_prompt: systemPrompt,
        model: agent.model,
        temperature: agent.temperature,
        max_tokens: agent.max_tokens,
        rag_config: agent.rag_config,
        is_active: false,
        notes
      })
      .select()
      .single();

    if (error) {
throw error;
}

    log.info('Prompt version created', { agentId, version: newVersion });
    return mapDatabaseToPromptVersion(version as DatabasePromptVersion);
  } catch (error) {
    log.error('Failed to create prompt version', { agentId }, error as Error);
    throw error;
  }
}

// Activate prompt version
export async function activatePromptVersion(
  agentId: string,
  versionId: string
): Promise<void> {
  try {
    log.info('Activating prompt version', { agentId, versionId });

    // Get the version details
    const { data: version } = await supabase
      .from('ai_text_chat_prompt_versions')
      .select('version, system_prompt')
      .eq('id', versionId)
      .single();

    if (!version) {
throw new Error('Version not found');
}

    // Deactivate all other versions
    await supabase
      .from('ai_text_chat_prompt_versions')
      .update({ is_active: false })
      .eq('agent_id', agentId);

    // Activate this version
    await supabase
      .from('ai_text_chat_prompt_versions')
      .update({ is_active: true })
      .eq('id', versionId);

    // Update agent with new prompt
    await supabase
      .from('ai_text_chat_agents')
      .update({
        system_prompt: version.system_prompt,
        prompt_version: version.version,
        updated_at: new Date().toISOString()
      })
      .eq('id', agentId);

    log.info('Prompt version activated', { agentId, versionId });
  } catch (error) {
    log.error('Failed to activate prompt version', { agentId, versionId }, error as Error);
    throw error;
  }
}

// Get agent statistics
export async function getAgentStatistics(agentId: string): Promise<any> {
  try {
    const { data: stats, error } = await supabase
      .from('ai_internal_chat_analytics')
      .select('*')
      .eq('agent_type', agentId)
      .order('timestamp', { ascending: false })
      .limit(100);

    if (error) {
throw error;
}

    return stats;
  } catch (error) {
    log.error('Failed to load agent statistics', { agentId }, error as Error);
    throw error;
  }
}

// Test agent configuration
export async function testAgentConfiguration(
  agentId: string,
  testQuery: string
): Promise<{
  response: string;
  responseTime: number;
  tokensInput: number;
  tokensOutput: number;
  cost: number;
  documentsUsed: any[];
}> {
  try {
    log.info('Testing agent configuration', { agentId, testQuery });

    // Get agent configuration from database
    const { data: agent, error: agentError } = await supabase
      .from('ai_text_chat_agents')
      .select('*')
      .eq('id', agentId)
      .single();

    if (agentError || !agent) {
      throw new Error('Agent configuration not found');
    }

    // Import testing service functions dynamically to avoid circular dependencies
    const { createTestSession, callTestQueryEdgeFunction } = await import('./aiTestingService');

    // Create a temporary test session
    const sessionId = await createTestSession([], 'manual');

    // Call edge function with real agent configuration
    const result = await callTestQueryEdgeFunction(
      testQuery,
      agent.model as ModelType,
      agent.agent_key as AgentType,
      sessionId
    );

    log.info('Agent test completed', {
      agentId,
      responseTime: result.responseTime,
      cost: result.cost
    });

    return {
      response: result.response,
      responseTime: result.responseTime,
      tokensInput: result.tokensInput,
      tokensOutput: result.tokensOutput,
      cost: result.cost,
      documentsUsed: result.documentsUsed || []
    };
  } catch (error) {
    log.error('Failed to test agent configuration', { agentId }, error as Error);
    throw error;
  }
}